import { EditNote, HowToReg, Timelapse } from "@mui/icons-material";
import { ListItemText } from "@mui/material";
import { darken, lighten } from "@mui/system";
import { format } from "date-fns";
import { normalizeConfigForTable, serverDate, serverToUi } from "../../../app/utils/utilityFunctions";
import no_content_img from "../../../asset/images/no_content.png";
import { generalTableConfig } from "../../../components/config/generalTableConfig";
import ResolveAttendanceDialog from "../ResolveAttendanceDialog";
import ResolveOvertimeDialog from "../ResolveOvertimeDialog";
import {
  conflictedAttendanceColumnView,
  enableFiltersOnConflictedAttendance,
  enableFiltersOnOvertimeAttendance,
  enableFiltersOnRoutineAttendance,
  overtimeAttendanceColumnView,
  routineAttendanceColumnView,
} from "./attendanceColumns";
import { ATTENDANCE_ADJUSTMENT, ATTENDANCE_OVERTIME, ROUTINE_ATTENDANCE } from "./attendanceUtils";

const createGroupConfig =
  (groupByValue = (v) => v) =>
  (rows) => {
    const transform = (data) => (data ? format(serverDate(data), "EEEE - MMMM d, yyyy") : "");
    const uniqueValues = [...new Set(rows.map(groupByValue))].sort((a, b) => serverDate(b) - serverDate(a));

    return {
      baseRowStyles: {
        borderLeft: "2px solid",
        borderRight: "2px solid",
        borderColor: "primary.main",
        backgroundColor: (theme) =>
          theme.palette.mode === "dark" ? darken(theme.palette.secondary.light, 0.675) : lighten(theme.palette.primary.light, 0.85),
        "& .MuiTypography-root": {
          color: "primary.main",
          fontWeight: 500,
          textAlign: "center",
          fontSize: "1.1rem",
        },
      },
      groups: uniqueValues.map((value) => ({
        id: `group_config-${value}`.toLowerCase(),
        groupText: transform(value),
        filterData: (rowData) => rowData.filter((row) => groupByValue(row) === value),
        noRecordsFoundUi: {
          src: no_content_img,
          altSrc: "No Data",
          title: `No Records for ${transform(value)}`,
          subTitle: "Try adjusting your filters",
        },
      })),
    };
  };

const attendanceTableConfig = (
  tableId,
  exportTableId,
  tableTitle,
  columnView,
  enableFiltersOnColumns = null,
  valueConfig = null,
  tableDialogComponent = null
) =>
  normalizeConfigForTable(
    generalTableConfig({
      tableId,
      exportTableId,
      tableTitle,
      tableViewColumns: columnView,
      preventDisplayOf: {
        selectSortDataByDisplay: false,
        selectDateRangeDisplay: false,
        filterOptionsDisplay: !!enableFiltersOnColumns,
        addRowButtonDisplay: false,
      },
      otherDefaultsVariables: {
        paginationTitle: "Employees / Page",
        ...(enableFiltersOnColumns && { enableFiltersOnColumns }),
      },
      paginationRowsOptions: {
        pageOptions: [10, 20, 30],
      },
      ...(valueConfig && {
        groupRows: createGroupConfig(valueConfig),
      }),
      ...(tableDialogComponent && {
        tableRowClickNavigate: {
          tableOpenDialogForRow: true,
          tableDialogComponent,
        },
      }),
    })
  );

const TableTypography = ({ title, activeDate, activeDateLabel, defaultDate }) => {
  return (
    <ListItemText
      primary={title}
      secondary={activeDate.filter(Boolean) ? activeDateLabel : defaultDate ? serverToUi(defaultDate) : "No Date Selected"}
      primaryTypographyProps={{ noWrap: true, fontSize: "1.24rem", fontWeight: 600, color: "primary" }}
      secondaryTypographyProps={{ variant: "h6", color: "text.primary" }}
    />
  );
};

export const attendanceTabData = {
  [ATTENDANCE_ADJUSTMENT]: {
    id: ATTENDANCE_ADJUSTMENT,
    label: "Attendance Adjustment",
    color: "error",
    getConfig: (activeDate, activeDateLabel) =>
      attendanceTableConfig(
        ATTENDANCE_ADJUSTMENT,
        "att_adj",
        <TableTypography title="Attendance Adjustment Data" activeDate={activeDate} activeDateLabel={activeDateLabel} />,
        conflictedAttendanceColumnView,
        enableFiltersOnConflictedAttendance,
        (row) => row?.["bus_dt"],
        (props) => <ResolveAttendanceDialog {...props} />
      ),
    get icon() {
      return <EditNote color={this.color} />;
    },
  },
  [ROUTINE_ATTENDANCE]: {
    id: ROUTINE_ATTENDANCE,
    label: "Attendance",
    color: "success",
    getConfig: (activeDate, activeDateLabel) =>
      attendanceTableConfig(
        ROUTINE_ATTENDANCE,
        "att_rt",
        <TableTypography title="Attendance Data" activeDate={activeDate} activeDateLabel={activeDateLabel} defaultDate={new Date()} />,
        routineAttendanceColumnView,
        enableFiltersOnRoutineAttendance,
        (row) => row?.["bus_dt"],
      ),
    get icon() {
      return <HowToReg color={this.color} />;
    },
  },
  [ATTENDANCE_OVERTIME]: {
    id: ATTENDANCE_OVERTIME,
    label: "Overtime",
    color: "warning",
    getConfig: (activeDate, activeDateLabel) =>
      attendanceTableConfig(
        ATTENDANCE_OVERTIME,
        "att_ot",
        <TableTypography title="Overtime Data" activeDate={activeDate} activeDateLabel={activeDateLabel} />,
        overtimeAttendanceColumnView,
        enableFiltersOnOvertimeAttendance,
        (row) => row?.["bus_dt"],
        (props) => <ResolveOvertimeDialog {...props} />
      ),
    get icon() {
      return <Timelapse color={this.color} />;
    },
  },
};
